import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MagnifyingGlass, Path, MapPin, ListNumbers, Truck, Warning,
  List as ListIcon, MapTrifold,
} from "@phosphor-icons/react";
import RouteMap from "../../components/routes/RouteMap";
import RouteMapGoogle from "../../components/routes/RouteMapGoogle";

const FREQ = ["", "weekly", "bi-weekly", "monthly", "on_demand"];
const VT = ["any", "2_wheeler", "3_wheeler", "4_wheeler", "6_wheeler_truck"];

export default function RouteCreate() {
  // Master data
  const [lsgds, setLsgds] = useState([]);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);  // default: show ALL with GPS
  const [staffList, setStaffList] = useState([]);        // active field staff
  const [vehicleList, setVehicleList] = useState([]);    // active vehicles
  const [loading, setLoading] = useState(false);

  // Filters (client-side for default set; server-side when LSGD picked)
  const [filters, setFilters] = useState({
    lsgd_id: "", zone_ids: [], ward_ids: [],
    service_frequency: "", vehicle_access: "any", limit: 500,
  });
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(new Set());

  // Route config
  const [start, setStart] = useState({ lat: "", lng: "", label: "" });
  const [end, setEnd] = useState({ lat: "", lng: "", label: "" });
  const [useCustomEnd, setUseCustomEnd] = useState(false);
  const [engine, setEngine] = useState("haversine");
  const [scheduledDate, setScheduledDate] = useState(
    new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  // Iter 131 — inline Save & Assign fields
  const [assignStaffId, setAssignStaffId] = useState("");
  const [assignVehicleId, setAssignVehicleId] = useState("");
  const [srHandling, setSrHandling] = useState("link_only");
  const [view, setView] = useState("map");   // map | list
  // Iter 130 — allow ops to flip between OSM (Leaflet) and Google Maps.
  // Persist across page loads so power users don't retoggle every time.
  const [mapProvider, setMapProvider] = useState(() =>
    localStorage.getItem("routeMapProvider") || "google");
  useEffect(() => {
    localStorage.setItem("routeMapProvider", mapProvider);
  }, [mapProvider]);
  const nav = useNavigate();

  // Load masters + all customers with GPS on mount
  useEffect(() => {
    api.get("/locations?type=lsgd").then(r => setLsgds(r.data || [])).catch(() => {});
    api.get("/zones").then(r => setZones(r.data || [])).catch(() => {});
    api.get("/locations?type=ward").then(r => setWards(r.data || [])).catch(() => {});
    api.get("/staff?status=active").then(r => setStaffList(r.data || [])).catch(() => {});
    api.get("/vehicles").then(r => setVehicleList((r.data || []).filter(v => v.status !== "inactive"))).catch(() => {});
    loadAll();
    // eslint-disable-next-line
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Iter 130 — use the dedicated /routes/all-customers endpoint so the
      // GPS + filter fields (lat/lng, lsgd_id, zone_id, ward_id,
      // service_frequency, vehicle_type) are always present. The plain
      // /customers projection strips these, which caused the filter &
      // selection to silently return an empty list.
      const r = await api.get("/routes/all-customers");
      const rows = (r.data || []).map(c => ({
        id: c.id, name: c.name, customer_code: c.customer_code, phone: c.phone,
        address: c.address || "", lat: c.lat, lng: c.lng,
        has_gps: true, ward_id: c.ward_id, zone_id: c.zone_id, lsgd_id: c.lsgd_id,
        vehicle_access: c.vehicle_access, vehicle_type: c.vehicle_type,
        service_frequency: c.service_frequency,
        has_open_sr: !!c.has_open_sr,
        open_sr_no: c.open_sr_no, open_sr_priority: c.open_sr_priority,
      }));
      setAllCustomers(rows);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const wardsForLsgd = useMemo(() =>
    wards.filter(w => w.parent_id === filters.lsgd_id), [wards, filters.lsgd_id]);

  // Client-side filter for live map/list updates
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allCustomers.filter(c => {
      if (filters.lsgd_id && c.lsgd_id !== filters.lsgd_id) return false;
      if (filters.zone_ids.length && !filters.zone_ids.includes(c.zone_id)) return false;
      if (filters.ward_ids.length && !filters.ward_ids.includes(c.ward_id)) return false;
      if (filters.service_frequency && c.service_frequency !== filters.service_frequency) return false;
      if (filters.vehicle_access !== "any" && c.vehicle_type !== filters.vehicle_access) return false;
      if (q) {
        const hay = `${c.name} ${c.phone || ""} ${c.customer_code || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).slice(0, filters.limit);
  }, [allCustomers, filters, search]);

  const withSelection = useMemo(() =>
    filtered.map(c => ({ ...c, selected: picked.has(c.id) })),
    [filtered, picked]);

  const togglePick = (id) => {
    const n = new Set(picked);
    n.has(id) ? n.delete(id) : n.add(id);
    setPicked(n);
  };
  const selectAll = () => setPicked(new Set(filtered.map(c => c.id)));
  const clearSel = () => setPicked(new Set());

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => setStart({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" }),
      () => toast.error("Could not get location"),
    );
  };

  const runPreview = async () => {
    if (picked.size === 0) { toast.error("Pick at least one customer"); return; }
    if (!start.lat || !start.lng) { toast.error("Set start point"); return; }
    setLoading(true);
    try {
      const body = {
        start: { lat: Number(start.lat), lng: Number(start.lng), label: start.label },
        end: useCustomEnd && end.lat && end.lng
          ? { lat: Number(end.lat), lng: Number(end.lng), label: end.label } : null,
        customer_ids: Array.from(picked), engine,
      };
      const r = await api.post("/routes/optimize", body);
      setPreview(r.data);
      toast.success(`Optimized · ${r.data.distance_km} km · ~${r.data.duration_min} min`);
    } catch (e) { toast.error(formatApiError(e)); setPreview(null); }
    setLoading(false);
  };

  const createJob = async (withAssign = false) => {
    if (!preview) { toast.error("Optimize route first (rule)"); return; }
    if (withAssign) {
      if (!assignStaffId) { toast.error("Pick a staff to Save & Assign"); return; }
      if (!assignVehicleId) { toast.error("Pick a vehicle to Save & Assign"); return; }
    }
    setCreating(true);
    try {
      const body = {
        name: name || null, scheduled_date: scheduledDate,
        filters: {
          ...filters,
          service_frequency: filters.service_frequency || null,
          limit: Math.min(filters.limit, 200),
        },
        start: { lat: Number(start.lat), lng: Number(start.lng), label: start.label },
        end: useCustomEnd && end.lat && end.lng
          ? { lat: Number(end.lat), lng: Number(end.lng), label: end.label } : null,
        engine, customer_ids: Array.from(picked),
        sr_handling: srHandling,
        ...(withAssign ? {
          assigned_staff_id: assignStaffId,
          assigned_vehicle_id: assignVehicleId,
        } : {}),
      };
      const r = await api.post("/routes/jobs", body);
      const sr = r.data?.sr_summary;
      const srMsg = sr ? ` · SRs: linked ${sr.linked}, generated ${sr.generated}` : "";
      const assignedMsg = withAssign ? " (assigned)" : " (draft)";
      toast.success(`Route ${r.data.job_no} created${assignedMsg}${srMsg}`);
      nav(`/operations/routes/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setCreating(false);
  };

  const startPt = start.lat && start.lng
    ? { lat: Number(start.lat), lng: Number(start.lng), label: start.label } : null;
  const endPt = useCustomEnd && end.lat && end.lng
    ? { lat: Number(end.lat), lng: Number(end.lng), label: end.label } : null;

  return (
    <AppShell title="Create Route Plan" subtitle="Filter → select → optimize → assign"
      actions={
        <button onClick={() => nav("/operations/routes")} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
          <ArrowLeft size={14}/>Back
        </button>
      }>
      <div className="grid grid-cols-12 gap-5">
        {/* Left — filters + selection summary */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="route-filters">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">Filters</div>
            <L>Search</L>
            <div className="relative mb-3">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
              <input value={search} onChange={e => setSearch(e.target.value)} data-testid="route-search"
                     placeholder="Name / phone / code"
                     className="w-full h-9 pl-7 pr-3 border border-[#E5E2D9] rounded-md text-xs"/>
            </div>
            <L>LSGD</L>
            <select value={filters.lsgd_id} onChange={e => setFilters(f => ({ ...f, lsgd_id: e.target.value, zone_ids: [], ward_ids: [] }))}
                    data-testid="route-lsgd" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs mb-3">
              <option value="">— any —</option>
              {lsgds.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <L>Health Zones</L>
            <MultiSelect options={zones} value={filters.zone_ids}
                          onChange={(v) => setFilters(f => ({ ...f, zone_ids: v }))}
                          testid="route-zones"/>
            <L className="mt-2">Wards</L>
            <MultiSelect options={wardsForLsgd} value={filters.ward_ids}
                          onChange={(v) => setFilters(f => ({ ...f, ward_ids: v }))}
                          testid="route-wards"/>
            <L className="mt-2">Service Frequency</L>
            <select value={filters.service_frequency} onChange={e => setFilters(f => ({ ...f, service_frequency: e.target.value }))}
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs mb-2">
              {FREQ.map(f => <option key={f} value={f}>{f || "— any —"}</option>)}
            </select>
            <L>Vehicle Access</L>
            <select value={filters.vehicle_access} onChange={e => setFilters(f => ({ ...f, vehicle_access: e.target.value }))}
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs mb-2">
              {VT.map(v => <option key={v} value={v}>{v.replaceAll("_", " ")}</option>)}
            </select>
            <L>Customer Limit</L>
            <input type="number" min={1} max={500} value={filters.limit}
                   onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value) || 500 }))}
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="route-selection-panel">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-2">Selection</div>
            <div className="text-xs mb-3">
              <b>{picked.size}</b> of {filtered.length} on screen
              <span className="text-[#8A8780]"> · {allCustomers.length} total with GPS</span>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={selectAll} data-testid="route-select-all" className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">Select all ({filtered.length})</button>
              <button onClick={clearSel} data-testid="route-clear-sel" className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">Clear</button>
            </div>
            <div className="max-h-40 overflow-y-auto text-xs space-y-1">
              {Array.from(picked).slice(0, 50).map(id => {
                const c = allCustomers.find(x => x.id === id);
                if (!c) return null;
                return (
                  <div key={id} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[#8A8780] w-20 truncate">{c.customer_code}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <button onClick={() => togglePick(id)} className="text-[#C25D38] text-[10px]">×</button>
                  </div>
                );
              })}
              {picked.size > 50 && <div className="text-[#8A8780]">… and {picked.size - 50} more</div>}
            </div>
          </div>
        </div>

        {/* Right — map/list + route config */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          {/* Toggle + stats header */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-3 flex items-center gap-3">
            <div className="flex gap-0">
              <button onClick={() => setView("map")} data-testid="route-view-map"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] rounded-l-md inline-flex items-center gap-1.5 ${view === "map" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>
                <MapTrifold size={12}/>Map
              </button>
              <button onClick={() => setView("list")} data-testid="route-view-list"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] border-l-0 rounded-r-md inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>
                <ListIcon size={12}/>List
              </button>
            </div>
            <div className="text-xs text-[#5C5A55]">
              Showing <b>{filtered.length}</b> customer{filtered.length !== 1 ? "s" : ""}
              {loading && <span className="ml-2 text-[#8A8780]">loading…</span>}
            </div>

            {/* Iter 130 — Leaflet ↔ Google Maps toggle */}
            <div className="ml-auto flex items-center gap-2" data-testid="route-map-provider-toggle">
              <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780]">Map</span>
              <div className="flex">
                <button
                  onClick={() => setMapProvider("google")}
                  data-testid="route-map-provider-google"
                  className={`h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded-l-md ${mapProvider === "google" ? "bg-[#1F3A57] text-white border-[#1F3A57]" : "bg-white text-[#5C5A55]"}`}>
                  Google
                </button>
                <button
                  onClick={() => setMapProvider("leaflet")}
                  data-testid="route-map-provider-leaflet"
                  className={`h-8 px-2.5 text-[11px] border border-[#E5E2D9] border-l-0 rounded-r-md ${mapProvider === "leaflet" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#5C5A55]"}`}>
                  Leaflet
                </button>
              </div>
            </div>
          </div>

          {view === "map" ? (
            mapProvider === "google" ? (
              <RouteMapGoogle customers={withSelection} start={startPt} end={endPt}
                              onPinClick={(c) => togglePick(c.id)} height="520px"/>
            ) : (
              <RouteMap customers={withSelection} start={startPt} end={endPt}
                        onPinClick={(c) => togglePick(c.id)} height="520px"/>
            )
          ) : (
            <div className="bg-white border border-[#E5E2D9] rounded-md max-h-[520px] overflow-y-auto" data-testid="route-customer-list">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#8A8780]">No customers match the filters.</div>
              ) : filtered.map(c => (
                <label key={c.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#F0ECE3] text-sm cursor-pointer hover:bg-[#FAF8F2]">
                  <input type="checkbox" checked={picked.has(c.id)} onChange={() => togglePick(c.id)}
                         data-testid={`route-pick-${c.id}`}/>
                  <span className="font-mono text-[10px] text-[#8A8780] w-24 truncate">{c.customer_code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.has_open_sr && (
                    <span title={`Open SR ${c.open_sr_no || ""} · ${c.open_sr_priority || "NORMAL"}`}
                          data-testid={`route-sr-badge-${c.id}`}
                          className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                            c.open_sr_priority === "URGENT" || c.open_sr_priority === "HIGH"
                              ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#DDE7F1] text-[#1F3A57]"}`}>
                      SR{c.open_sr_priority && c.open_sr_priority !== "NORMAL" ? ` · ${c.open_sr_priority}` : ""}
                    </span>
                  )}
                  <span className="text-xs text-[#5C5A55]">{c.phone}</span>
                </label>
              ))}
            </div>
          )}

          {/* Route config */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="route-config">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">Route Configuration</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <L>Start Point (lat, lng) *</L>
                <div className="flex gap-2">
                  <input value={start.lat} onChange={e => setStart(s => ({ ...s, lat: e.target.value }))}
                         placeholder="Lat (e.g., 9.93)" data-testid="route-start-lat"
                         className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
                  <input value={start.lng} onChange={e => setStart(s => ({ ...s, lng: e.target.value }))}
                         placeholder="Lng (e.g., 76.26)" data-testid="route-start-lng"
                         className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
                  <button onClick={useMyLocation} title="Use my location"
                          className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm inline-flex items-center gap-1">
                    <MapPin size={12}/>GPS
                  </button>
                </div>
                <input value={start.label} onChange={e => setStart(s => ({ ...s, label: e.target.value }))}
                       placeholder="Label (e.g., Kazhakkottam Drop Point)"
                       className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-xs mt-2"/>
              </div>

              <div className="col-span-2">
                <label className="inline-flex items-center gap-2 text-xs text-[#5C5A55] mb-2 cursor-pointer">
                  <input type="checkbox" checked={useCustomEnd} onChange={e => setUseCustomEnd(e.target.checked)}
                         data-testid="route-end-toggle"/>
                  End point different from start?
                </label>
                {useCustomEnd && (
                  <div className="flex gap-2">
                    <input value={end.lat} onChange={e => setEnd(s => ({ ...s, lat: e.target.value }))}
                           placeholder="End Lat" className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
                    <input value={end.lng} onChange={e => setEnd(s => ({ ...s, lng: e.target.value }))}
                           placeholder="End Lng" className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
                    <input value={end.label} onChange={e => setEnd(s => ({ ...s, label: e.target.value }))}
                           placeholder="Label" className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
                  </div>
                )}
              </div>

              <div>
                <L>Optimization Engine</L>
                <select value={engine} onChange={e => setEngine(e.target.value)} data-testid="route-engine"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                  <option value="haversine">In-house (Haversine NN)</option>
                  <option value="google">Google Directions API</option>
                </select>
              </div>

              <div>
                <L>Scheduled Date *</L>
                <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                       data-testid="route-date"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>

              <div className="col-span-2">
                <L>Name (optional)</L>
                <input value={name} onChange={e => setName(e.target.value)}
                       placeholder="e.g., Mon · Kazhakkottam AM run"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>

              {/* Iter 131 — inline staff + vehicle picker for Save & Assign */}
              <div>
                <L>Field Staff (for Save &amp; Assign)</L>
                <select value={assignStaffId} onChange={e => setAssignStaffId(e.target.value)}
                        data-testid="route-assign-staff"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                  <option value="">— pick staff —</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.phone ? ` · ${s.phone}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <L>Vehicle (for Save &amp; Assign)</L>
                <select value={assignVehicleId} onChange={e => setAssignVehicleId(e.target.value)}
                        data-testid="route-assign-vehicle"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                  <option value="">— pick vehicle —</option>
                  {vehicleList.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.vehicle_no || v.registration_no || v.id}
                      {v.vehicle_type ? ` · ${v.vehicle_type.replaceAll("_", " ")}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <L>Service Request handling</L>
                <div className="flex flex-wrap gap-3 text-xs">
                  {[
                    { v: "link_only", label: "Link open SRs (don't auto-generate)" },
                    { v: "link_and_generate", label: "Link open SRs + auto-generate for others" },
                    { v: "none", label: "Skip — don't touch SR list" },
                  ].map(o => (
                    <label key={o.v} className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="sr-handling" value={o.v}
                             checked={srHandling === o.v}
                             onChange={() => setSrHandling(o.v)}
                             data-testid={`route-sr-handling-${o.v}`}/>
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-5 items-center flex-wrap">
              <button onClick={runPreview} disabled={loading || picked.size === 0}
                      data-testid="route-optimize-btn"
                      className="h-11 px-4 bg-white border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded-md text-sm font-medium inline-flex items-center gap-2">
                <Path size={14}/>Optimize Route
              </button>
              <button onClick={() => createJob(false)} disabled={!preview || creating}
                      data-testid="route-create-btn" title={preview ? "" : "Optimize first (rule)"}
                      className="h-11 px-4 bg-white border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] disabled:opacity-60 rounded-md text-sm font-medium inline-flex items-center gap-2">
                <Truck size={14}/>{creating ? "Saving…" : "Save as Draft"}
              </button>
              <button onClick={() => createJob(true)}
                      disabled={!preview || creating || !assignStaffId || !assignVehicleId}
                      data-testid="route-save-assign-btn"
                      title={
                        !preview ? "Optimize first (rule)"
                        : !assignStaffId ? "Pick a staff first"
                        : !assignVehicleId ? "Pick a vehicle first"
                        : "Save the plan + assign it in one click"
                      }
                      className="h-11 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
                <Truck size={14}/>{creating ? "Saving…" : "Save & Assign"}
              </button>
              {!preview && picked.size > 0 && (
                <span className="text-[11px] text-[#C25D38] inline-flex items-center gap-1">
                  <Warning size={10}/>Rule: optimize before creating
                </span>
              )}
            </div>
          </div>

          {preview && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-3" data-testid="route-preview">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] inline-flex items-center gap-1.5">
                  <ListNumbers size={12}/>Optimized Sequence · {preview.engine}
                </div>
                <div className="text-xs text-[#5C5A55]">{preview.distance_km} km · ~{preview.duration_min} min · {preview.stop_count} stops</div>
              </div>
              {mapProvider === "google" ? (
                <RouteMapGoogle
                  route={preview.stops}
                  start={preview.start} end={preview.end}
                  height="360px"/>
              ) : (
                <RouteMap
                  route={preview.stops}
                  start={preview.start} end={preview.end}
                  height="360px"/>
              )}
              <ol className="space-y-1">
                {preview.stops.map(s => (
                  <li key={s.seq} className="flex items-center gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#D4E0D1] text-[#2D5A27] text-xs font-bold flex items-center justify-center">{s.seq}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.customer_name}</div>
                      <div className="text-xs text-[#5C5A55] truncate">{s.customer_code}</div>
                    </div>
                    <a href={s.nav_url} target="_blank" rel="noreferrer"
                       className="text-[#2D5A27] text-xs hover:underline">navigate</a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

const L = ({ children, className = "" }) => (
  <label className={`text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2 ${className}`}>{children}</label>
);

const MultiSelect = ({ options, value, onChange, testid }) => {
  const toggle = (id) => {
    const s = new Set(value);
    s.has(id) ? s.delete(id) : s.add(id);
    onChange(Array.from(s));
  };
  return (
    <div className="border border-[#E5E2D9] rounded-md max-h-24 overflow-y-auto bg-white" data-testid={testid}>
      {options.length === 0 ? (
        <div className="p-2 text-[11px] text-[#8A8780]">— none —</div>
      ) : options.map(o => (
        <label key={o.id} className="flex items-center gap-2 px-2 py-1 text-[11px] hover:bg-[#FAF8F2] cursor-pointer">
          <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)}/>
          <span className="truncate">{o.name}</span>
        </label>
      ))}
    </div>
  );
};
